import ExcelJS from 'exceljs';
import {
  CODIGO_BONO_PRODUCTIVIDAD,
  CODIGO_SUELDO_BASE,
  normalizarMonto,
} from './actualizacionMasivaIngresosCalculo';

export { CODIGO_BONO_PRODUCTIVIDAD };

/** Encabezado de columna de concepto en Excel → código PLAME de 4 dígitos. */
export function extraerCodigoPlameDeEncabezadoExcel(texto) {
  const s = String(texto ?? '').trim();
  const cuatro = s.match(/^(\d{4})\b/);
  if (cuatro) return cuatro[1];
  if (/\b0121\b/.test(s) || (/sueldo/i.test(s) && /b[aá]sico/i.test(s))) {
    return CODIGO_SUELDO_BASE;
  }
  return null;
}

export function encabezadoColumnaConceptoExcel(codigo, descripcion) {
  const desc = String(descripcion || '').trim();
  return desc ? `${codigo} — ${desc}` : String(codigo);
}

export function normalizarDocumento(val) {
  return String(val ?? '')
    .trim()
    .replace(/\s/g, '');
}

/** Texto para columna pensión (ONP o AFP + nombre + tipo comisión). */
export function etiquetaAfpPension(emp) {
  const sys = String(emp?.sistema_pensionario || 'ONP').trim();
  if (sys.toUpperCase() === 'ONP') return 'ONP';
  if (sys.toUpperCase() === 'AFP') {
    let nombre = emp?.afp?.nombre || '';
    nombre = String(nombre).replace(/^AFP\s+/i, '').trim();
    const tipo = emp?.tipo_afp ? ` · ${emp.tipo_afp}` : '';
    return nombre ? `AFP ${nombre}${tipo}` : `AFP${tipo}`;
  }
  return sys || '—';
}

export function etiquetaRegimenLaboral(emp) {
  return emp?.regimen_laboral?.nombre || '—';
}

/**
 * Lee hoja exportada (o compatible) y devuelve filas { documento, montosPorCodigo }.
 */
export async function leerFilasIngresosDesdeExcel(file) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);

  const ws =
    wb.getWorksheet('Ingresos masivo') ||
    wb.getWorksheet('Ingresos') ||
    wb.worksheets[0];

  if (!ws) {
    throw new Error('El archivo no tiene hojas válidas');
  }

  const headerRow = ws.getRow(1);
  const colDocumento = [];
  const colCodigos = [];

  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const titulo = String(cell.value ?? '').trim();
    const lower = titulo.toLowerCase();
    if (lower === 'documento' || lower === 'dni' || lower === 'numero_documento') {
      colDocumento.push(colNumber);
      return;
    }
    const codigo = extraerCodigoPlameDeEncabezadoExcel(titulo);
    if (codigo) {
      colCodigos.push({ colNumber, codigo });
    }
  });

  if (!colDocumento.length) {
    throw new Error('No se encontró la columna "Documento" en la fila 1');
  }
  if (!colCodigos.length) {
    throw new Error(
      'No se encontraron columnas de conceptos PLAME (ej. "0121 — …" o "0902")'
    );
  }

  const docCol = colDocumento[0];
  const filas = [];

  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const documento = normalizarDocumento(row.getCell(docCol).value);
    if (!documento) return;

    const montosPorCodigo = {};
    colCodigos.forEach(({ colNumber, codigo }) => {
      const raw = row.getCell(colNumber).value;
      if (raw === null || raw === undefined || raw === '') return;
      const n = normalizarMonto(raw);
      montosPorCodigo[codigo] = n;
    });

    if (Object.keys(montosPorCodigo).length > 0) {
      filas.push({ documento, montosPorCodigo });
    }
  });

  return { filas, columnasDetectadas: colCodigos.map((c) => c.codigo) };
}

/**
 * Aplica filas del Excel al estado de celdas (match por número de documento).
 */
export function aplicarFilasExcelACeldas(empleados, celdas, filasExcel, ingresosPlameFn) {
  const porDocumento = new Map();
  empleados.forEach((emp) => {
    const doc = normalizarDocumento(emp.numero_documento);
    if (doc) porDocumento.set(doc, emp);
  });

  let filasOk = 0;
  let celdasActualizadas = 0;
  const noEncontrados = [];
  const sinConcepto = [];

  const nextCeldas = { ...celdas };
  const nextSueldos = {};

  filasExcel.forEach(({ documento, montosPorCodigo }) => {
    const emp = porDocumento.get(documento);
    if (!emp) {
      noEncontrados.push(documento);
      return;
    }
    filasOk += 1;

    Object.entries(montosPorCodigo).forEach(([codigo, monto]) => {
      const concepto = ingresosPlameFn(emp).find(
        (c) => String(c.plame_configuracion?.codigo_plame) === String(codigo)
      );
      if (!concepto) {
        sinConcepto.push(`${documento}:${codigo}`);
        return;
      }
      const prev = nextCeldas[concepto.id] || celdas[concepto.id];
      if (!prev) return;
      nextCeldas[concepto.id] = { ...prev, monto };
      celdasActualizadas += 1;
      if (codigo === CODIGO_SUELDO_BASE) {
        nextSueldos[emp.id] = monto;
      }
    });
  });

  return {
    celdas: nextCeldas,
    sueldosBasico: nextSueldos,
    filasOk,
    celdasActualizadas,
    noEncontrados,
    sinConcepto,
  };
}

export async function escribirWorkbookIngresosMasivo({
  filasEmpleado,
  columnasIngreso,
  descripcion0121,
  nombreEmpresa,
}) {
  const wb = new ExcelJS.Workbook();

  const wsCat = wb.addWorksheet('Conceptos PLAME');
  wsCat.addRow(['Código PLAME', 'Descripción', 'Tipo', 'Notas']);
  wsCat.getRow(1).font = { bold: true };
  wsCat.addRow([
    CODIGO_SUELDO_BASE,
    descripcion0121 || 'Remuneración o jornal básico',
    'Ingreso',
    'Al guardar actualiza el sueldo básico del trabajador',
  ]);
  columnasIngreso.forEach((col) => {
    const nota =
      col.codigo === CODIGO_BONO_PRODUCTIVIDAD
        ? 'Al guardar actualiza «Tiene bono» y monto bono en la ficha'
        : 'Solo si el trabajador tiene este concepto';
    wsCat.addRow([col.codigo, col.descripcion || '', 'Ingreso', nota]);
  });
  wsCat.columns.forEach((c) => {
    c.width = 22;
  });
  wsCat.getColumn(2).width = 48;

  const ws = wb.addWorksheet('Ingresos masivo');
  ws.addRow([
    'Documento',
    'Nombre completo',
    'Cargo',
    'Proyecto',
    'Categoría',
    'Régimen laboral',
    'Sistema pensionario / AFP',
    'Estado',
    encabezadoColumnaConceptoExcel(CODIGO_SUELDO_BASE, descripcion0121),
    ...columnasIngreso.map((c) => encabezadoColumnaConceptoExcel(c.codigo, c.descripcion)),
  ]);
  ws.getRow(1).font = { bold: true };

  filasEmpleado.forEach((row) => ws.addRow(row));

  ws.columns.forEach((col) => {
    col.width = 16;
  });
  ws.getColumn(2).width = 36;

  const wsAyuda = wb.addWorksheet('Cómo importar');
  wsAyuda.addRow(['Paso', 'Instrucción']);
  wsAyuda.getRow(1).font = { bold: true };
  wsAyuda.addRow([
    '1',
    'Edite montos en la hoja "Ingresos masivo". No cambie la columna Documento.',
  ]);
  wsAyuda.addRow([
    '2',
    'Las columnas de conceptos empiezan con el código PLAME (ej. 0121 — …, 0902 — …).',
  ]);
  wsAyuda.addRow(['3', 'Vea descripciones en la hoja "Conceptos PLAME".']);
  wsAyuda.addRow([
    '4',
    'En el modal use "Importar Excel", luego "Guardar y recalcular".',
  ]);
  wsAyuda.getColumn(1).width = 8;
  wsAyuda.getColumn(2).width = 70;

  const buffer = await wb.xlsx.writeBuffer();
  return { buffer, nombreEmpresa };
}
